import React, { useCallback, useState } from 'react';
import Tabs, { TabNav, TabNavItem, TabPanel, TabPanels } from '../../../../../ui/DataDisplay/Tabs/Tabs';
import LineChart, { Point } from '../../../../../ui/DataDisplay/Charts/LineChart/LineChart';
import { SongTiktokStats } from '@round/api';
import styles from './SongCreationsGraph.module.css';
import { formatNumberToCommaNotation } from '../../../../../helpers';

type SongCreationsGraphProps = {
    data: SongTiktokStats['standardised_data'] | undefined;
    loading?: boolean;
};

const SongCreationsGraph = ({ data, loading }: SongCreationsGraphProps) => {
    const [activeTab, setActiveTab] = useState<'total' | 'daily'>('total');

    const renderTooltip = useCallback(({ total, daily }: { total?: number; daily?: number }) => {
        return (
            <div className={styles.tooltipContent}>
                <span className={styles.tooltipDot} />
                <div>
                    {typeof total === 'number' && (
                        <p>
                            Total Creations:
                            <span className={styles.tooltipValue}>{formatNumberToCommaNotation(total)}</span>
                        </p>
                    )}
                    {typeof daily === 'number' && (
                        <p>
                            Daily Creations:
                            <span className={styles.tooltipValue}>{formatNumberToCommaNotation(daily)}</span>
                        </p>
                    )}
                </div>
            </div>
        );
    }, []);

    const renderTotalCreationsTooltip = useCallback(
        (point: Point) => {
            const dailyCreations = data?.daily_total_video_counts.find((d) => d.timestamp === point.x);
            return renderTooltip({ total: point.y, daily: dailyCreations?.change });
        },
        [data?.daily_total_video_counts, renderTooltip]
    );

    const renderDailyCreationsTooltip = useCallback(
        (point: Point) => {
            const totalCreations = data?.daily_total_video_counts.find((t) => t.timestamp === point.x);
            return renderTooltip({ total: totalCreations?.video_count, daily: point.y });
        },
        [data?.daily_total_video_counts, renderTooltip]
    );

    return (
        <div>
            <Tabs activeTab={activeTab} onChange={setActiveTab}>
                <TabNav>
                    <TabNavItem name="total">Total creations</TabNavItem>
                    <TabNavItem name="daily">Daily creations</TabNavItem>
                </TabNav>

                <TabPanels>
                    <TabPanel name="total">
                        <LineChart
                            title="Total creations"
                            points={
                                data?.daily_total_video_counts.map((item) => ({
                                    x: item.timestamp,
                                    y: item.video_count,
                                })) ?? []
                            }
                            loading={Boolean(loading)}
                            renderTooltip={renderTotalCreationsTooltip}
                        />
                    </TabPanel>
                    <TabPanel name="daily">
                        <LineChart
                            title="Daily creations"
                            points={
                                data?.daily_total_video_counts.map((item) => ({
                                    x: item.timestamp,
                                    y: item.change,
                                })) ?? []
                            }
                            loading={Boolean(loading)}
                            renderTooltip={renderDailyCreationsTooltip}
                        />
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </div>
    );
};

export default SongCreationsGraph;
